<template>
  <div class="comments-thread">
    <div v-if="loading" class="text-center py-3">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading comments...</span>
      </div>
    </div>

    <div v-else-if="comments && comments.length > 0" class="comments-list mb-4">
      <div v-for="comment in sortedComments" :key="comment.id" class="comment-item mb-3">
        <div class="d-flex">
          <div class="avatar avatar-sm me-3">
            <span class="avatar-title rounded-circle bg-primary">
              {{ getInitials(getUserName(comment.user)) }}
            </span>
          </div>

          <div class="flex-grow-1">
            <div class="comment-content p-3 rounded bg-light">
              <div class="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
                <div>
                  <strong>{{ getUserName(comment.user) }}</strong>
                  <span v-if="comment.visibility" class="badge bg-secondary ms-2">
                    {{ formatVisibility(comment.visibility) }}
                  </span>
                </div>
                <div class="d-flex align-items-center gap-2 ms-auto">
                  <small class="text-muted">{{ formatDate(comment.created_at) }}</small>
                  <button
                    v-if="canDelete(comment)"
                    class="btn btn-link btn-sm text-danger p-0 delete-btn"
                    :disabled="deletingId === comment.id"
                    @click="requestDelete(comment)"
                    title="Delete comment"
                    aria-label="Delete comment"
                  >
                    <span v-if="deletingId === comment.id" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="bi bi-trash"></i>
                  </button>
                </div>
              </div>

              <p class="mb-0">{{ comment.comment }}</p>

              <!-- Reply Button -->
              <button
                v-if="canReply"
                @click="toggleReply(comment)"
                class="btn btn-sm btn-link text-muted p-0 mt-2 reply-btn"
              >
                <i class="bi bi-arrow-90deg-left fw-bolder" style="font-size: 0.85rem; color: cornflowerblue;"></i> Reply
              </button>

              <!-- Inline Reply Composer -->
              <div v-if="activeReplyId === comment.id" class="mt-2">
                <textarea
                  v-model="replyDrafts[comment.id]"
                  class="form-control form-control-sm mb-2"
                  rows="2"
                  placeholder="Write a reply..."
                ></textarea>
                <div class="d-flex justify-content-end gap-2 flex-wrap">
                  <button type="button" class="btn btn-light btn-sm" @click="cancelInlineReply">Cancel</button>
                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    :disabled="submittingReply || !replyDrafts[comment.id] || !replyDrafts[comment.id].trim()"
                    @click="submitReply(comment)"
                  >
                    <span v-if="submittingReply" class="spinner-border spinner-border-sm me-2"></span>
                    Post Reply
                  </button>
                </div>
              </div>
            </div>

            <!-- Nested Replies -->
            <div v-if="comment.replies && comment.replies.length > 0" class="replies ms-4 mt-2">
              <div v-for="reply in comment.replies" :key="reply.id" class="reply-item mb-2">
                <div class="d-flex">
                  <div class="avatar avatar-sm me-2">
                    <span class="avatar-title rounded-circle bg-secondary">
                      {{ getInitials(getUserName(reply.user)) }}
                    </span>
                  </div>

                  <div class="flex-grow-1">
                    <div class="reply-content p-2 rounded bg-white border">
                      <div class="d-flex justify-content-between align-items-start mb-1 flex-wrap gap-2">
                        <strong class="small">{{ getUserName(reply.user) }}</strong>
                        <div class="d-flex align-items-center gap-2 ms-auto">
                          <small class="text-muted">{{ formatDate(reply.created_at) }}</small>
                          <button
                            v-if="canDelete(reply)"
                            class="btn btn-link btn-sm text-danger p-0 delete-btn"
                            :disabled="deletingId === reply.id"
                            @click="requestDelete(reply)"
                            title="Delete reply"
                            aria-label="Delete reply"
                          >
                            <span v-if="deletingId === reply.id" class="spinner-border spinner-border-sm"></span>
                            <i v-else class="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                      <p class="mb-0 small">{{ reply.comment }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-4 text-muted">
      <i class="icon-feather-message-circle fs-1 mb-2"></i>
      <p>No comments yet. Be the first to add a comment!</p>
    </div>

    <!-- Add Comment Form (Top-level) -->
    <div class="add-comment-form card border-0 shadow-sm rounded-3">
      <div class="card-body">
        <form @submit.prevent="submitComment">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">
                <i class="icon-feather-message-circle me-1"></i>
                Add Comment
              </label>
              <textarea
                v-model="newComment.text"
                class="form-control form-control-lg rounded-3 shadow-sm"
                rows="1"
                :maxlength="maxChars"
                placeholder="Share your thoughts or feedback..."
                required
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <small class="text-muted">Your comment will be shared based on visibility</small>
                <small class="text-muted">{{ newCommentChars }} / {{ maxChars }}</small>
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label">Visibility</label>
              <div class="input-group">
                <span class="input-group-text"><i class="bi bi-eye"></i></span>
                <select v-model="newComment.visibility" class="form-select form-select-lg">
                  <option value="all">All (Everyone)</option>
                  <option value="private">Private</option>
                  <option value="employee">Employees</option>
                  <option value="manager">Managers</option>
                  <option value="hr">HR</option>
                </select>
              </div>
              <small class="text-muted">Choose who can see this comment</small>
            </div>
          </div>

          <div class="row g-3">

            <div class="col-md-12 text-md-end align-self-end">
              <div class="d-flex flex-wrap justify-content-end gap-2">
                <button
                  type="button"
                  @click="resetForm"
                  class="btn btn-outline-secondary btn-sm"
                >
                  Clear
                </button>
                <button
                  type="submit"
                  class="btn btn-primary btn-sm"
                  :disabled="submitting || !newComment.text.trim()"
                >
                  <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="icon-feather-send me-2"></i>
                  Post Comment
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import moment from 'moment';
import notify from '@/utils/notify';
import {decrypt} from "@/utils/crypto";

export default {
  name: 'CommentsThread',
  props: {
    comments: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['add', 'delete'],
  setup(props, { emit }) {
    const submitting = ref(false);
    const submittingReply = ref(false);
    const activeReplyId = ref(null);
    const replyDrafts = ref({});
    const deletingId = ref(null);
    const newComment = ref({
      text: '',
      visibility: 'all'
    });

    const maxChars = 1000;
    const newCommentChars = computed(() => (newComment.value.text || '').length);

    const userRole = computed(() => localStorage.getItem('user_role'));
    const authUserId = computed(() => {
      let profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
      return (profile.id);
    });
    const canReply = computed(() => true); // Can be customized based on permissions

    const sortedComments = computed(() => {
      if (!props.comments) return [];

      // Group comments and replies
      const mainComments = props.comments.filter(c => !c.parent_id);
      const repliesMap = {};

      props.comments.filter(c => c.parent_id).forEach(reply => {
        if (!repliesMap[reply.parent_id]) {
          repliesMap[reply.parent_id] = [];
        }
        repliesMap[reply.parent_id].push(reply);
      });

      // Sort replies chronologically and attach to main comments
      Object.keys(repliesMap).forEach(pid => {
        repliesMap[pid].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      });

      return mainComments
        .map(comment => ({
          ...comment,
          replies: repliesMap[comment.id] || []
        }))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });

    const getUserName = (user) => {
      if (!user) return 'Unknown';
      return user.full_name || [user.first_name, user.last_name].filter(Boolean).join(' ') || user.name || 'Unknown';
    };

    const getInitials = (name) => {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const formatDate = (date) => {
      const momentDate = moment(date);
      const now = moment();
      const diffHours = now.diff(momentDate, 'hours');

      if (diffHours < 1) {
        const diffMinutes = now.diff(momentDate, 'minutes');
        if (diffMinutes < 1) return 'Just now';
        return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffHours < 48) {
        return 'Yesterday';
      } else {
        return momentDate.format('MMM DD, YYYY');
      }
    };

    const formatVisibility = (visibility) => {
      const visibilityMap = {
        // New enum values
        all: 'All',
        private: 'Private',
        employee: 'Employee',
        managers: 'Managers',
        hr: 'HR',
        // Backward compatibility
        manager_hr: 'Managers & HR',
        hr_only: 'HR Only'
      };
      return visibilityMap[visibility] || visibility;
    };

    const resetForm = () => {
      newComment.value = {
        text: '',
        visibility: 'all'
      };
    };

    const toggleReply = (comment) => {
      if (activeReplyId.value === comment.id) {
        activeReplyId.value = null;
      } else {
        activeReplyId.value = comment.id;
        if (!replyDrafts.value[comment.id]) replyDrafts.value[comment.id] = '';
      }
    };

    const cancelInlineReply = () => {
      activeReplyId.value = null;
    };

    const submitComment = async () => {
      if (!newComment.value.text.trim()) {
        notify.alert.error('Please enter a comment');
        return;
      }

      submitting.value = true;

      try {
        const payload = {
          comment: newComment.value.text,
          visibility: newComment.value.visibility,
          parent_id: null
        };

        emit('add', payload);

        notify.alert.success('Comment posted successfully');
        resetForm();
      } catch (error) {
        console.error('Failed to post comment:', error);
        notify.alert.error('Failed to post comment');
      } finally {
        submitting.value = false;
      }
    };

    const submitReply = async (parentComment) => {
      const text = replyDrafts.value[parentComment.id] || '';
      if (!text.trim()) {
        notify.alert.error('Please enter a reply');
        return;
      }

      submittingReply.value = true;

      try {
        const payload = {
          comment: text,
          visibility: parentComment.visibility || 'all',
          parent_id: parentComment.id
        };

        emit('add', payload);

        notify.alert.success('Reply posted successfully');
        replyDrafts.value[parentComment.id] = '';
        activeReplyId.value = null;
      } catch (error) {
        console.error('Failed to post reply:', error);
        notify.alert.error('Failed to post reply');
      } finally {
        submittingReply.value = false;
      }
    };

    const canDelete = (item) => {
      if (!item) return false;
      const ownerId = item.user_id || item.user?.id;
      return ownerId && authUserId.value && String(ownerId) === String(authUserId.value);
    };

    const requestDelete = async (item) => {
      if (!item?.id) return;
      const ok = window.confirm('Delete this comment? This action cannot be undone.');
      if (!ok) return;
      try {
        deletingId.value = item.id;
        emit('delete', item.id);
      } finally {
        // Let parent handle actual API, we immediately re-enable to keep UI responsive
        deletingId.value = null;
      }
    };

    return {
      submitting,
      submittingReply,
      deletingId,
      newComment,
      maxChars,
      newCommentChars,
      userRole,
      authUserId,
      canReply,
      canDelete,
      requestDelete,
      activeReplyId,
      replyDrafts,
      sortedComments,
      getUserName,
      getInitials,
      formatDate,
      formatVisibility,
      resetForm,
      toggleReply,
      cancelInlineReply,
      submitComment,
      submitReply
    };
  }
};
</script>

<style scoped>
.avatar {
  width: 40px;
  height: 40px;
}

.avatar-sm {
  width: 32px;
  height: 32px;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
}

.comment-content {
  position: relative;
}

.comment-content::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 12px;
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 8px solid #f8f9fa;
}

.reply-content {
  font-size: 0.9rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.reply-item:hover .reply-content {
  border-color: #0d6efd; /* Bootstrap primary */
  box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.15);
}

/* Delete icon button hover */
.delete-btn:hover {
  color: #bb2d3b !important; /* Bootstrap danger dark */
  filter: brightness(0.95);
}

/* Reply button hover/focus highlight */
.reply-btn {
  border-radius: 0.25rem;
  transition: background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.reply-btn:hover,
.reply-btn:focus {
  text-decoration: none;
  background-color: rgba(13, 110, 253, 0.08);
  box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.15);
}

  .replies {
    border-left: 2px solid #e9ecef;
    padding-left: 1rem;
  }

  /* Text wrapping to prevent overflow for long words/URLs */
  .comment-content,
  .reply-content {
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  /* Mobile adjustments */
  @media (max-width: 576px) {
    .avatar {
      width: 32px;
      height: 32px;
    }
    .avatar-sm {
      width: 24px;
      height: 24px;
    }

    .comment-content {
      padding: 0.75rem;
    }

    .reply-content {
      padding: 0.5rem;
      font-size: 0.85rem;
    }

    .replies {
      border-left-width: 1px;
      padding-left: 0.5rem;
    }

    /* Stack form buttons */
    .add-comment-form .btn {
      width: 100%;
    }
    .add-comment-form .btn + .btn {
      margin-top: 0.5rem;
      margin-left: 0 !important;
    }

    /* Compact reply button on small screens */
    .reply-btn {
      padding: 0.25rem 0.25rem;
    }
  }
</style>
